import Vue from 'vue';
import * as types from '../mutation-types';
import ContactAPI from '../../api/contacts';
import ConversationApi from '../../api/conversations';

export const createMessagePayload = (payload, message) => {
  const { content, cc_emails, bcc_emails } = message;
  payload.append('message[content]', content);
  if (cc_emails) payload.append('message[cc_emails]', cc_emails);
  if (bcc_emails) payload.append('message[bcc_emails]', bcc_emails);
};

export const createConversationPayload = ({ params, contactId, files }) => {
  const { inboxId, message, sourceId, mailSubject, assigneeId } = params;
  const payload = new FormData();

  if (message) {
    createMessagePayload(payload, message);
  }

  if (files && files.length > 0) {
    files.forEach(file => payload.append('message[attachments][]', file));
  }

  payload.append('inbox_id', inboxId);
  payload.append('contact_id', contactId);
  payload.append('source_id', sourceId);
  if (mailSubject) {
    payload.append('additional_attributes[mail_subject]', mailSubject);
  }
  payload.append('assignee_id', assigneeId);

  return payload;
};

export const createWhatsAppConversationPayload = ({ params }) => {
  const { inboxId, message, contactId, sourceId, assigneeId } = params;

  const payload = {
    inbox_id: inboxId,
    contact_id: contactId,
    source_id: sourceId,
    message,
    assignee_id: assigneeId,
  };

  return payload;
};

const setNewConversationPayload = ({
  isFromWhatsApp,
  params,
  contactId,
  files,
}) => {
  if (isFromWhatsApp) {
    return createWhatsAppConversationPayload({ params });
  }
  return createConversationPayload({
    params,
    contactId,
    files,
  });
};

const state = {
  records: {},
  uiFlags: {
    isFetching: false,
  },
};

const findContactConversationLocation = ($state, conversationId) => {
  const normalizedConversationId = Number(conversationId);

  return Object.entries($state.records).find(([, conversations]) =>
    conversations.some(conversation => conversation.id === normalizedConversationId)
  );
};

export const getters = {
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getContactConversation: $state => id => {
    return $state.records[Number(id)] || [];
  },
};

export const actions = {
  create: async ({ commit }, { params, isFromWhatsApp }) => {
    commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
      isCreating: true,
    });
    const { contactId, files } = params;
    try {
      const payload = setNewConversationPayload({
        isFromWhatsApp,
        params,
        contactId,
        files,
      });

      const { data } = await ConversationApi.create(payload);
      commit(types.default.ADD_CONTACT_CONVERSATION, {
        id: contactId,
        data,
      });

      return data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
        isCreating: false,
      });
    }
  },
  get: async ({ commit }, contactId) => {
    commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
      isFetching: true,
    });
    try {
      const response = await ContactAPI.getConversations(contactId);
      commit(types.default.SET_CONTACT_CONVERSATIONS, {
        id: contactId,
        data: response.data.payload,
      });
      commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
        isFetching: false,
      });
    } catch (error) {
      commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
        isFetching: false,
      });
    }
  },
  updateConversation: ({ commit }, conversation) => {
    const contactId = conversation?.meta?.sender?.id;
    if (!contactId) {
      return;
    }

    commit(types.default.ADD_CONTACT_CONVERSATION, {
      id: contactId,
      data: conversation,
    });
  },
  addMessage: ({ commit }, message) => {
    commit('ADD_CONTACT_CONVERSATION_MESSAGE', message);
  },
};

export const mutations = {
  [types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.default.SET_CONTACT_CONVERSATIONS]: ($state, { id, data }) => {
    Vue.set($state.records, id, data);
  },
  [types.default.ADD_CONTACT_CONVERSATION]: ($state, { id, data }) => {
    const conversations = $state.records[id] || [];

    const updatedConversations = [...conversations];
    const index = conversations.findIndex(
      conversation => conversation.id === data.id
    );

    if (index !== -1) {
      updatedConversations[index] = { ...conversations[index], ...data };
    } else {
      updatedConversations.push(data);
    }

    Vue.set($state.records, id, updatedConversations);
  },
  ADD_CONTACT_CONVERSATION_MESSAGE: ($state, message) => {
    const foundConversation = findContactConversationLocation(
      $state,
      message.conversation_id
    );

    if (!foundConversation) {
      return;
    }

    const [contactId, conversations] = foundConversation;
    const updatedConversations = [...conversations];
    const conversationIndex = updatedConversations.findIndex(
      conversation => conversation.id === Number(message.conversation_id)
    );

    if (conversationIndex === -1) {
      return;
    }

    const targetConversation = updatedConversations[conversationIndex];
    const existingMessages = Array.isArray(targetConversation.messages)
      ? [...targetConversation.messages]
      : [];
    const messageIndex = existingMessages.findIndex(
      existingMessage => existingMessage.id === message.id
    );

    if (messageIndex !== -1) {
      existingMessages[messageIndex] = {
        ...existingMessages[messageIndex],
        ...message,
      };
    } else {
      existingMessages.push(message);
    }

    updatedConversations[conversationIndex] = {
      ...targetConversation,
      messages: existingMessages,
      last_activity_at:
        message.conversation?.last_activity_at || targetConversation.last_activity_at,
      timestamp:
        message.conversation?.last_activity_at ||
        message.created_at ||
        targetConversation.timestamp,
      unread_count:
        message.conversation?.unread_count ?? targetConversation.unread_count,
    };

    Vue.set($state.records, contactId, updatedConversations);
  },
  [types.default.DELETE_CONTACT_CONVERSATION]: ($state, id) => {
    Vue.delete($state.records, id);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
